import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  Instagram,
  MapPin,
  Phone,
  Wrench,
  Facebook,
  Youtube,
} from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AUTOMOTIVE_ENTRIES } from './AutomotiveServices';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=600&h=400&fit=crop';

// Same key function AutomotiveServices.tsx uses for its managed collection,
// so the slug in the URL always matches the entry the grid card linked from.
export function autoSlug(e: any, i: number) {
  return slugify(e.name || String(i));
}

// Collect every contact/social URL a listing has, deduped, with a label and
// icon — mirrors the button logic on the grid cards.
function collectContacts(e: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: e.website, label: 'Website' },
    { url: e.socialMediaUrl },
    { url: e.instagram, label: 'Instagram' },
    { url: e.facebook, label: 'Facebook' },
    { url: e.youtube, label: 'YouTube' },
    { url: e.tiktok, label: 'TikTok' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      return { ...c, Icon: Globe, label: c.label || 'Visit Page' };
    });
}

function mapsUrl(e: any) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address || 'Dallas, TX'}`)}`;
}

export default function AutomotiveDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('automotive_services', AUTOMOTIVE_ENTRIES, (e, i) => autoSlug(e, i));

  const entry = mc.items.find((e: any, i: number) => autoSlug(e, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Automotive Service Not Found | Dallas Desi Scoop"
          description="This automotive service listing could not be found."
          canonical="/automotive-services"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Automotive service listing not found.</p>
        <Link to="/automotive-services" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to Automotive Services
        </Link>
      </div>
    );
  }

  const name = entry.name || 'Automotive Service';
  const image = entry.image || FALLBACK_IMAGE;
  const description = (entry.description || '').trim();
  const phone = (entry.phone || '').trim();
  const address = (entry.address || '').trim();
  const contacts = collectContacts(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const canonicalPath = `/automotive-services/${slug}`;

  // Data-driven schema type: listings whose own description says dealership
  // get AutoDealer, everything else gets AutoRepair.
  const isDealer = /dealership|dealer\b/i.test(description);
  const schemaType = isDealer ? 'AutoDealer' : 'AutoRepair';

  const title = `${name} | Automotive Services in Dallas–Fort Worth | Dallas Desi Scoop`;
  const metaDescription = description
    ? `${name} — ${description}. Find address, phone, website, and service details for this DFW automotive business on Dallas Desi Scoop.`
    : `${name} is an automotive service provider serving the Dallas–Fort Worth metroplex. Find contact details and location on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': schemaType,
      '@id': `${BASE_URL}${canonicalPath}#business`,
      name,
      image,
      description: description
        ? `${name}, ${description}, serving the Dallas–Fort Worth metroplex.`
        : `${name}, automotive services provider serving the Dallas–Fort Worth metroplex.`,
      telephone: phone || undefined,
      url: contacts[0]?.url || `${BASE_URL}${canonicalPath}`,
      address: address
        ? {
            '@type': 'PostalAddress',
            streetAddress: address,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : {
            '@type': 'PostalAddress',
            addressLocality: 'Dallas',
            addressRegion: 'TX',
            addressCountry: 'US',
          },
      areaServed: 'Dallas–Fort Worth metroplex',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Automotive Services', item: `${BASE_URL}/automotive-services` },
        { '@type': 'ListItem', position: 3, name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other automotive listings in DFW.
  const related = mc.items
    .map((e: any, i: number) => ({ entry: e, key: autoSlug(e, i) }))
    .filter((r: any) => r.key !== slug)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/automotive-services"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Automotive Services
            </Link>
          </motion.div>

          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1 text-[11px] text-white/70 mb-3 flex-wrap"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/automotive-services" className="hover:text-white transition-colors">Automotive Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white font-medium">{name}</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="text-white text-[26px] md:text-[34px] font-bold leading-tight"
            data-hs-id="automotive-detail-h1"
          >
            {name}
          </motion.h1>

          {description && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="text-white/90 text-[13px] md:text-[15px] mt-2 max-w-[640px]"
            >
              {description}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.14 }}
            className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-[12px] text-white/85"
          >
            {address && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#f59e0b]" /> {address}
              </span>
            )}
            {phone && (
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-[#51ba4f]" /> {phone}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-white/70" /> Serving Dallas–Fort Worth
            </span>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1000px] w-[calc(100%-40px)] mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-6"
            >
              <h2 className="text-[18px] font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Wrench className="w-4.5 h-4.5 text-[#082f6d]" /> About {name}
              </h2>
              <p className="text-[14px] text-gray-600 leading-relaxed">
                {description ||
                  `${name} is an automotive service provider serving the South Asian community and drivers across the Dallas–Fort Worth metroplex.`}
              </p>
              <p className="text-[14px] text-gray-600 leading-relaxed mt-3">
                Dallas Desi Scoop connects the DFW Desi community with trusted local businesses.
                Contact {name} directly for service details, hours, and quotes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.08 }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-6"
            >
              <h2 className="text-[18px] font-bold text-gray-900 mb-3">Services &amp; Offerings</h2>
              {description ? (
                <p className="text-[14px] text-gray-600 leading-relaxed">{description}</p>
              ) : (
                <p className="text-[14px] text-gray-500">
                  [Coming soon] — service details for this listing have not been added yet.
                </p>
              )}
              <p className="text-[13px] text-gray-500 mt-3">
                Call or visit the business directly for a full list of services, pricing, and
                availability across Dallas, Plano, Frisco, Irving, and the wider DFW area.
              </p>
            </motion.div>
          </div>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="flex flex-col gap-6"
          >
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-[16px] font-bold text-gray-900 mb-4">Contact &amp; Info</h2>
              <div className="flex flex-col gap-3">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                    className="flex items-center justify-center gap-2 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <Phone className="w-4 h-4" /> Call {phone}
                  </a>
                )}
                {entry.website && (
                  <a
                    href={entry.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Visit Website
                  </a>
                )}
                {contacts.map((c) => (
                  <a
                    key={c.url}
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <c.Icon className="w-4 h-4" /> {c.label}
                  </a>
                ))}
                {address && (
                  <a
                    href={mapsUrl(entry)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> View on Google Maps
                  </a>
                )}
                {!phone && !entry.website && contacts.length === 0 && !address && (
                  <p className="text-[13px] text-gray-500">
                    [Coming soon] — contact details for this listing have not been added yet.
                  </p>
                )}
              </div>
              {hasAnySocial && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <SocialLinks item={entry} px={28} />
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-[16px] font-bold text-gray-900 mb-3">Location</h2>
              {address ? (
                <>
                  <p className="text-[13px] text-gray-600 leading-relaxed">{address}</p>
                  <a
                    href={mapsUrl(entry)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#082f6d] hover:underline mt-2"
                  >
                    <MapPin className="w-3.5 h-3.5" /> Get Directions
                  </a>
                </>
              ) : (
                <p className="text-[13px] text-gray-500">
                  [Coming soon] — address for this listing has not been added yet.
                </p>
              )}
            </div>
          </motion.aside>
        </div>

        {/* Related listings */}
        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="text-[18px] font-bold text-gray-900 mb-4">
              More Automotive Services in DFW
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {related.map((r: any) => (
                <a
                  key={r.key}
                  href={`/automotive-services/${r.key}`}
                  className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group"
                >
                  <div className="w-full h-[110px] bg-gray-100 overflow-hidden">
                    <img
                      src={r.entry.image || FALLBACK_IMAGE}
                      alt={r.entry.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-2.5">
                    <h3 className="text-[12px] font-bold text-[#082f6d] leading-tight group-hover:underline">
                      {r.entry.name}
                    </h3>
                    {r.entry.address && (
                      <p className="text-[10px] text-gray-500 mt-1 truncate">{r.entry.address}</p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Internal links */}
        <div className="mt-10 bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { to: '/restaurants', label: 'Indian Restaurants in DFW' },
              { to: '/events', label: 'Desi Events & Concerts' },
              { to: '/deals', label: 'Local Deals' },
              { to: '/grocery', label: 'Indian Grocery Stores' },
              { to: '/automotive-services', label: 'All Automotive Services' },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[12px] font-medium text-[#082f6d] bg-blue-50 hover:bg-blue-100 rounded-full px-3.5 py-1.5 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
